/**
 * PerformanceIndex — « Mon indice » (barre latérale).
 *
 * Spec iter281 (2026-09-02) :
 *   • Icône : 5 anneaux concentriques (viewBox 72×72), un par module ACTIF,
 *     du plus grand au plus petit : Tâches r32, Documents r26.4,
 *     Validation r20.8, Accusé r15.2, Information r9.6. Fond #1e1e20,
 *     trait coloré 2.2 px, linecap round, rempli au % réel du module.
 *   • Dessous : « Mon indice » puis le % Global (modules actifs) coloré.
 *   • Clic → panneau « Performance par module » (liste verticale, un module
 *     par ligne, mini-anneau + %). Admin / Premier Admin : sélecteur
 *     d'utilisateur à droite du titre pour afficher l'indice d'un collègue.
 *   • CRM et Pointage restent « à venir » (0 fixe, hors Global).
 *
 * Données : GET /api/me/performance[?user_id=…].
 */
import { useEffect, useState } from "react";
import DeskTip from "@/components/common/DeskTip";
import { Zap, Sparkles, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const ACTIVE_RINGS = [
  { key: "taches",      label: "Tâches",      color: "#F9BC26", r: 32 },
  { key: "documents",   label: "Documents",   color: "#7bb3e8", r: 26.4 },
  { key: "validations", label: "Validation",  color: "#8a8fe0", r: 20.8 },
  { key: "accuses",     label: "Accusé",      color: "#F9A826", r: 15.2 },
  { key: "information", label: "Information", color: "#2a9a7a", r: 9.6 },
];
const COMING_SOON = [
  { key: "crm",      label: "Commercial", color: "#9aa0a6" },
  { key: "tresorerie", label: "Trésorerie", color: "#168570" },
  { key: "pointage", label: "Pointage", color: "#7CA982" },
];
const TRACK = "#3a3a3e";
const HAS_DATA_KEY = { taches: "taches_has_data", documents: "documents_has_data",
  validations: "validations_has_data", accuses: "accuses_has_data", information: "information_has_data" };

export function scoreColor(score) {
  if (score >= 80) return "#22C55E";
  if (score >= 50) return "#FF8C00";
  return "#EF4444";
}

function Ring({ r, color, value }) {
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <>
      <circle cx="36" cy="36" r={r} fill="none" stroke={TRACK} strokeWidth="2.2" />
      <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="2.2"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.7s ease" }} />
    </>
  );
}

function MiniRing({ value, color, dimmed = false }) {
  const r = 12, c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" className="-rotate-90 shrink-0" aria-hidden="true">
      <circle cx="16" cy="16" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
      <circle cx="16" cy="16" r={r} fill="none" stroke={color} strokeWidth="3" strokeDasharray={c}
        strokeDashoffset={off} strokeLinecap="round"
        style={{ opacity: dimmed ? 0.5 : 1, transition: "stroke-dashoffset 0.6s ease" }} />
    </svg>
  );
}


function ModuleRow({ m, value, hasData, mocked }) {
  const dim = mocked || !hasData;
  return (
    <div className="flex items-center gap-3" data-testid={`perf-row-${m.key}`}>
      <MiniRing value={dim ? 0 : value} color={m.color} dimmed={dim} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px] font-medium truncate">{m.label}</span>
          <span className="text-[13px] font-semibold tabular-nums" style={{ color: m.color, opacity: dim ? 0.55 : 1 }}>
            {mocked || !hasData ? "—" : `${value}%`}
          </span>
        </div>
        {dim && (
          <div className="text-[10px] italic text-muted-foreground leading-tight">
            {mocked ? "à venir" : "aucune donnée (90 j)"}
          </div>
        )}
      </div>
    </div>
  );
}

/** Carte « Performance — Prénom Nom » (chantier 3) : réutilisée par la barre (ex-Mon indice) et par « Action par personne » du Dashboard. */
export function PerformanceCard({ perf = {}, title = "Performance par module" }) {
  return (
          <div
            className="rounded-2xl border border-white/10 p-4 w-[300px] text-white"
            style={{ background: "rgba(30,30,30,0.97)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset" }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-vertec-blue shrink-0" strokeWidth={2.5} />
              <span className="text-sm font-semibold tracking-tight whitespace-nowrap">{title}</span>
            </div>
            <div className="space-y-2.5">
              {ACTIVE_RINGS.map((m) => (
                <ModuleRow key={m.key} m={m} value={Math.max(0, Math.min(100, (perf.breakdown || {})[m.key] ?? 0))}
                  hasData={perf[HAS_DATA_KEY[m.key]] !== false} mocked={false} />
              ))}
              {COMING_SOON.map((m) => (
                <ModuleRow key={m.key} m={m} value={0} hasData={false} mocked />
              ))}
            </div>
            <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: "1px dashed rgba(255,255,255,0.14)" }}
              data-testid="perf-row-global">
              <Sparkles size={15} className="shrink-0" style={{ color: scoreColor(perf.global_score ?? 0) }} />
              <span className="flex-1 text-sm font-semibold">Global</span>
              <span className="text-base font-bold tabular-nums" style={{ color: scoreColor(perf.global_score ?? 0) }}>
                {perf.global_score ?? 0}%
              </span>
            </div>
            <div className="pt-2 mt-2 text-[10px] text-muted-foreground italic leading-relaxed">
              Fenêtre 90 jours. Global = moyenne des modules actifs ayant des données ; Commercial et Pointage « à venir » sont exclus.
            </div>
          </div>
  );
}

export default function PerformanceIndex({ external = null, compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchNow = () =>
      api.get("/me/performance")
        .then(({ data }) => { if (alive) setData(data); })
        .catch(() => {});
    fetchNow();
    const id = setInterval(fetchNow, 30000);
    const onFocus = () => fetchNow();
    window.addEventListener("focus", onFocus);
    window.addEventListener("desk:data-reset", onFocus);
    return () => {
      alive = false; clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("desk:data-reset", onFocus);
    };
  }, []);

  // Spec iter283 — `external` = { breakdown, global, label } fourni par le
  // Dashboard pour suivre la vue sélectionnée (Globale / autre utilisateur).
  const breakdown = external ? (external.breakdown ?? {}) : (data?.breakdown ?? {});
  const globalScore = external ? (external.global ?? null) : (data?.global_score ?? null);
  const viewingOther = !!external?.label;
  const targetName = external?.label || "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <DeskTip text={viewingOther ? `Indice de ${targetName}` : "Mon indice — cliquer pour le détail"} side="right" testId="perf-index-tooltip">
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-col items-center justify-center gap-1 py-1 w-full rounded-lg hover:bg-white/5 transition-colors focus:outline-none"
          data-testid="perf-index"
        >
          {compact ? (
            <div className="relative" style={{ width: 60, height: 60 }} data-testid="perf-index-compact-ring">
              <svg viewBox="0 0 72 72" width="60" height="60" className="-rotate-90">
                <Ring r={32} color={scoreColor(globalScore ?? 0)} value={globalScore ?? 0} />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[15px] font-bold tabular-nums leading-none"
                style={{ color: scoreColor(globalScore ?? 0) }} data-testid="perf-index-score">
                {globalScore === null ? "—" : `${globalScore}%`}
              </span>
            </div>
          ) : (
          <svg viewBox="0 0 72 72" width="72" height="72" className="-rotate-90" data-testid="perf-index-svg">
            {ACTIVE_RINGS.map((m) => (
              <Ring key={m.key} r={m.r} color={m.color} value={external ? (breakdown[m.key] ?? 0) : (data?.[HAS_DATA_KEY[m.key]] === false ? 0 : (breakdown[m.key] || 0))} />
            ))}
          </svg>
          )}
          <span className="text-[11px] text-muted-foreground truncate max-w-[84px]">{external?.label || (viewingOther ? "Indice" : "Mon indice")}</span>
          {!compact && globalScore !== null && (
            <span
              className="text-[13px] font-bold tabular-nums leading-none"
              style={{ color: scoreColor(globalScore) }}
              data-testid="perf-index-score"
            >
              {globalScore}%
            </span>
          )}
        </button>
      </PopoverTrigger>
      </DeskTip>
      {data && (
        <PopoverContent side="right" sideOffset={12} align="center"
          className="p-0 bg-transparent border-0 shadow-none w-auto" data-testid="perf-index-tooltip">
          <PerformanceCard perf={{ breakdown, global_score: globalScore, ...(data || {}) }} title={viewingOther ? `Performance — ${targetName}` : "Performance par module"} />
        </PopoverContent>
      )}
    </Popover>
  );
}
